/**
 * Reconciling the Service row with the offering pill.
 *
 * Three surfaces on this screen name a procedure, from two different facts:
 * the header pill and the Treatment section both render the **booked** offering
 * off a procedure appointment, while the Service row carries **stated intent**.
 *
 * The design wants that divergence visible — a lead who came in wanting IGB and
 * booked ESG is the most interesting fact on the panel. But the pill is an
 * unlabelled coloured chip, so two chips that quietly disagree read as a bug
 * rather than as information, and a rep who has just *edited* Service reads the
 * unchanged pill as a failed save. Naming the booked procedure on the row is
 * what makes the disagreement legible.
 */

import { Appointment } from '@medplum/fhirtypes';
import { getOfferingCode, isIgbRemoval } from '../../../PatientTimelineHeader/offeringColors';

/**
 * The offering the header pill is showing, or undefined when no procedure is
 * booked — which is the normal case for a lead.
 *
 * Deliberately the same derivation the header uses (first non-IGB-removal
 * procedure, `_sort: -date`, offering read off the appointmentType display), so
 * the row can never disagree with the pill about what *is* booked. If the header
 * changes how it picks, this must follow.
 */
export function bookedOffering(procedures: Appointment[]): string | undefined {
  const primary = procedures.find((appointment) => !isIgbRemoval(appointment.appointmentType?.coding?.[0]?.display));
  if (!primary) return undefined;

  return getOfferingCode(primary.appointmentType?.coding?.[0]?.display) ?? undefined;
}

/**
 * The muted suffix on the Service row: empty when there is nothing to
 * reconcile, otherwise names what is actually booked.
 *
 * Shown when they differ *and* when intent was never recorded — in the latter
 * case "Not set" beside a booked ESG otherwise reads as missing data rather
 * than as a question nobody asked.
 */
export function serviceNote(service: string | undefined, booked: string | undefined): string {
  if (!booked || service === booked) return '';

  return `· booked ${booked}`;
}
